import PageLayout from '@/components/shared/PageLayout';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { createClient } from './services/clients-api';
import React, { useState } from "react";
import { useCanAccess } from '@/components/authnz/AbilityContext';
import { DatePickerField } from "@/components/ui/date-picker-field";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { cn, formatPhoneInternational } from "@/lib/utils";
import { useSettings } from "@/components/settings/lib/settings-context";
import { HeightDisplay, WeightDisplay } from "@/components/ui/unit-display";
import { convertHeightToMetric, convertWeightToMetric } from "./lib/unit-conversion";
import { useIsMobile } from "@/hooks/use-mobile";
import { PAIN_SCALE_NRS10 } from "@/lib/clients/roster-fields";
import { PainLevelNrsSliderField } from "@/components/clients/fields/PainLevelNrsSliderField";

const SEX_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say'] as const;

const formSchema = z.object({
  name: z.string().min(1, { message: "Full name is required." }),
  email: z.preprocess(
    (val: unknown) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
    z.string().email({ message: "Invalid email address." }).optional()
  ),
  height: z.coerce.number().positive({ message: "Height must be a positive number." }),
  dateOfBirth: z.date().optional(),
  sex: z.enum(SEX_OPTIONS).optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
  weight: z.preprocess((val: unknown) => val === '' ? undefined : val, z.coerce.number().positive({ message: "Weight must be a positive number." }).optional()),
  classification: z.string().optional(),
  medicalHistory: z.string().optional(),
  injuryHistory: z.string().optional(),
  goals: z.string().optional(),
  liftingExperience: z.number().min(0).max(3).optional(),
  painLevel: z.number().min(0).max(10).optional(),
});
import logger from '@/lib/logging';

const experienceLevels = ["Beginner Lifter", "Novice Lifter", "Intermediate Lifter", "Athlete"];

export default function NewClientPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { settings } = useSettings();
  const { unit } = settings;
  const isMobile = useIsMobile();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;
  const canReadClient = useCanAccess('read', 'Client');
  const prefillName = searchParams.get('name')?.trim() ?? '';
  const prefillEmail = searchParams.get('email')?.trim() ?? '';
  const sourceProspectId = searchParams.get('prospectId')?.trim() ?? '';

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema) as any,
    defaultValues: {
      name: prefillName,
      address: "",
      phone: "",
      email: prefillEmail,
      sex: undefined,
      height: 0,
      weight: '' as any,
      classification: "",
      medicalHistory: "",
      injuryHistory: "",
      goals: "",
      liftingExperience: 0,
      painLevel: undefined,
    }
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);

    // Convert height and weight to metric values (always convert from display units to metric)
    const heightInCM = convertHeightToMetric(values.height, settings.unit);
    const weightInKG = typeof values.weight === 'number'
      ? convertWeightToMetric(values.weight, settings.unit)
      : undefined;

    // Sanitize data: remove undefined values and convert empty strings to null.
    // painLevel is handled separately below — a never-touched slider must
    // produce a doc with NO painLevel/painScale, not an affirmative "0/10".
    const sanitizedValues = Object.entries(values).reduce((acc, [key, value]) => {
      if (key === 'painLevel') return acc;
      if (value !== undefined && value !== '') {
        acc[key as keyof typeof values] = value;
      }
      return acc;
    }, {} as { [K in keyof typeof values]: any });

    // Replace height and weight with metric values, but only include weight if it's defined
    const finalValues: any = {
      ...sanitizedValues,
      height: heightInCM,
    };

    // Only include weight if it's defined (not undefined)
    if (weightInKG !== undefined) {
      finalValues.weight = weightInKG;
    }

    // Phase 6.3 — only write painLevel/painScale when the practitioner actually
    // moved the slider; an untouched form must not fabricate a "0/10 pain-free"
    // claim. Stamp the NRS marker whenever painLevel IS written so read sites
    // never mis-map this 0–10 value through the legacy 0–2 table.
    if (values.painLevel !== undefined) {
      finalValues.painLevel = values.painLevel;
      finalValues.painScale = PAIN_SCALE_NRS10;
    }

    try {
      const clientId = await createClient({
        ...finalValues,
        ...(sourceProspectId ? { sourceProspectId } : {}),
      } as any);

      toast({
        title: "Client Created",
        description: `${values.name} has been successfully added to your client list.`,
      });
      navigate(canReadClient ? `/clients/${clientId}` : '/sales/prospects');
    } catch (error) {
      logger.error("Error adding document: ", error);
      toast({
        title: "Error",
        description: "Failed to create client. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  }

  return (
    <PageLayout
      title="Create New Client"
      subtitle="Enter the details for your new client below"
      headerActions={
        <Button variant="ghost" size="sm" asChild>
          <Link to={canReadClient ? '/clients' : '/sales/prospects'}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">{canReadClient ? 'Back to Clients' : 'Back to Prospects'}</span>
          </Link>
        </Button>
      }
    >
      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader className="pb-4">
            {/* Mobile Step Indicator */}
            {isMobile && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Step {currentStep} of {totalSteps}</span>
                  <span className="text-xs text-muted-foreground">{Math.round((currentStep / totalSteps) * 100)}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Step 1: Basic Information */}
                <div className={cn("space-y-6", isMobile && currentStep !== 1 && "hidden")}>
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name <span className="text-error-text">*</span></FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. John Doe" {...field} className="h-12" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="dateOfBirth"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Date of Birth</FormLabel>
                          <FormControl>
                            <DatePickerField
                              ref={field.ref}
                              name={field.name}
                              onBlur={field.onBlur}
                              value={field.value}
                              onChange={field.onChange}
                              maxDate={new Date()}
                              minDate={new Date("1900-01-01")}
                              placeholder="Pick a date"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="sex"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Sex</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value ?? ""}>
                            <FormControl>
                              <SelectTrigger className="h-12">
                                <SelectValue placeholder="Select sex" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {SEX_OPTIONS.map((opt) => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Address</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. 123 Main St" {...field} className="h-12" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl>
                            <Input
                              type="tel"
                              placeholder="e.g. +13108958981"
                              {...field}
                              className="h-12"
                              onBlur={(e) => {
                                const formatted = formatPhoneInternational(e.target.value);
                                if (formatted) {
                                  field.onChange(formatted);
                                }
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="e.g. john.doe@email.com" {...field} className="h-12" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="height"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Height <span className="text-error-text">*</span></FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                name="height"
                                type="number"
                                placeholder="e.g. 180"
                                value={
                                  field.value
                                    ? (typeof field.value === 'number'
                                      ? field.value.toFixed(0)
                                      : field.value)
                                    : ''
                                }
                                onChange={e => field.onChange(e.target.value === '' ? '' : e.target.valueAsNumber)}
                                className="flex-1 h-12 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                step="1"
                                inputMode="decimal"
                              />
                              <div className="text-sm text-muted-foreground min-w-[2rem]">
                                {settings.unit === 'inches' ? 'in' : 'cm'}
                              </div>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="weight"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Weight</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                name="weight"
                                type="number"
                                placeholder="e.g. 75"
                                value={
                                  field.value
                                    ? (typeof field.value === 'number'
                                      ? field.value.toFixed(0)
                                      : field.value)
                                    : ''
                                }
                                onChange={e => field.onChange(e.target.value === '' ? '' : e.target.valueAsNumber)}
                                className="flex-1 h-12 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                step="1"
                                inputMode="decimal"
                              />
                              <div className="text-sm text-muted-foreground min-w-[2rem]">
                                {settings.unit === 'inches' ? 'lbs' : 'kg'}
                              </div>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="classification"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client Classification</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. ATP, Swayback, R Lateralization, R Grounding" {...field} className="h-12" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Step 2: Medical History */}
                <div className={cn("space-y-6", isMobile && currentStep !== 2 && "hidden")}>
                  <FormField
                    control={form.control}
                    name="medicalHistory"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Medical History</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Client's medical history..."
                            className="min-h-[120px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="liftingExperience"
                    render={({ field }) => (
                      <FormItem>
                        <div>
                          <FormLabel>Lifting Experience: <span className="font-semibold text-primary">{experienceLevels[field.value ?? 0]}</span></FormLabel>
                          <FormControl>
                            <Slider
                              min={0}
                              max={3}
                              step={1}
                              value={[field.value ?? 0]}
                              onValueChange={(value) => field.onChange(value[0])}
                            />
                          </FormControl>
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="painLevel"
                    render={({ field }) => (
                      <PainLevelNrsSliderField value={field.value} onChange={field.onChange} />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="injuryHistory"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Injury History</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Note any relevant past or current injuries..."
                            className="min-h-[120px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Step 3: Goals */}
                <div className={cn("space-y-6", isMobile && currentStep !== 3 && "hidden")}>
                  <FormField
                    control={form.control}
                    name="goals"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Goals</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="What are the client's primary training goals?"
                            className="min-h-[120px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Mobile Step Navigation */}
                {isMobile ? (
                  <div className="flex gap-3 pt-6 border-t">
                    {currentStep > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="flex-1 h-12"
                      >
                        <ChevronLeft className="h-4 w-4 mr-2" />
                        Previous
                      </Button>
                    )}
                    {currentStep < totalSteps ? (
                      <Button
                        type="button"
                        onClick={() => {
                          // Validate current step fields before proceeding
                          const stepFields: Record<number, (keyof z.infer<typeof formSchema>)[]> = {
                            1: ['name', 'email', 'dateOfBirth', 'sex', 'address', 'phone', 'height', 'weight', 'classification'],
                            2: ['medicalHistory', 'injuryHistory', 'liftingExperience', 'painLevel'],
                            3: ['goals'],
                          };

                          const fieldsToValidate = stepFields[currentStep] || [];
                          form.trigger(fieldsToValidate as any).then((isValid) => {
                            if (isValid) {
                              setCurrentStep(currentStep + 1);
                            }
                          });
                        }}
                        className="flex-1 h-12"
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    ) : (
                      <Button type="submit" disabled={isSubmitting} className="flex-1 h-12">
                        {isSubmitting ? "Saving..." : "Save Client"}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="flex justify-end">
                    <Button type="submit" disabled={isSubmitting} className="h-12">
                      {isSubmitting ? "Saving..." : "Save Client"}
                    </Button>
                  </div>
                )}
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
